import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Table,
  Tag,
  Space,
  Descriptions,
  Popconfirm,
  App,
  Collapse,
  Spin,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supplierDebitNoteService } from '@/services/supplierDebitNote.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { SupplierDebitNoteLineDTO } from '@/services/supplierDebitNote.service';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { getFinanceApiErrorMessage } from '@/utils/financeApiError';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

const { Title, Text } = Typography;

const statusColors: Record<string, string> = {
  Draft: 'default',
  Submitted: 'blue',
  Approved: 'green',
  Posted: 'cyan',
  Cancelled: 'red',
};

export default function SupplierDebitNoteDetailPage() {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const { data: dn, isLoading } = useQuery({
    queryKey: ['supplier-debit-note', id],
    queryFn: () => supplierDebitNoteService.findById(id!),
    enabled: !!id,
  });

  const statusLabel = (status: string) => {
    const map: Record<string, string> = {
      Draft: t('statusDraft'),
      Submitted: t('statusSubmitted'),
      Approved: t('statusApproved'),
      Posted: t('statusPosted'),
      Cancelled: t('statusCancelled'),
    };
    return map[status] ?? status;
  };

  const debitNoteTypeLabel = (type: string | undefined | null) => {
    if (!type) return '—';
    const map: Record<string, string> = {
      CommercialAdjustment: t('debitNoteTypeCommercialAdjustment'),
      RateDifference: t('debitNoteTypeRateDifference'),
      Other: t('debitNoteTypeOther'),
    };
    return map[type] ?? type;
  };

  const runAction = async (key: string, fn: () => Promise<unknown>) => {
    setActionLoading(key);
    try {
      await fn();
      message.success(tc('done'));
      queryClient.invalidateQueries({ queryKey: ['supplier-debit-note', id] });
      queryClient.invalidateQueries({ queryKey: ['supplier-debit-notes'] });
    } catch (e: unknown) {
      const text = getFinanceApiErrorMessage(e, tc('actionFailed'));
      if (/5002|2000|Chart of accounts|Accounts Payable|must be (Submitted|Approved|greater)/i.test(text)) {
        message.open({
          type: 'warning',
          content: text,
          duration: 10,
        });
      } else {
        message.error(text, 6);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const breadcrumbSegments = useMemo(
    () =>
      dn
        ? [
            { title: t('financeModule'), path: '/fin/dashboard' },
            { title: t('debitNotes'), path: '/fin/debit-notes' },
            { title: dn.debit_note_number || t('debitNote') },
          ]
        : null,
    [dn?.id, dn?.debit_note_number, t]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const sdnWorkflowStages = useMemo(() => {
    if (!dn) return [];
    const st = dn.status;
    const cancelled = st === 'Cancelled';
    return [
      {
        title: t('statusDraft'),
        description: t('workflowEntry'),
        status: (cancelled ? 'error' : st === 'Draft' ? 'process' : 'finish') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: t('statusSubmitted'),
        description: t('workflowReview'),
        status: (cancelled ? 'wait' : st === 'Submitted' ? 'process' : ['Approved', 'Posted'].includes(st) ? 'finish' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: t('statusApproved'),
        description: t('workflowReadyToPost'),
        status: (cancelled ? 'wait' : st === 'Approved' ? 'process' : st === 'Posted' ? 'finish' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
      {
        title: t('statusPosted'),
        description: t('workflowGl'),
        status: (cancelled ? 'wait' : st === 'Posted' ? 'finish' : 'wait') as 'wait' | 'process' | 'finish' | 'error',
      },
    ];
  }, [dn, t]);

  const sdnWorkflowCurrent = useMemo(() => {
    if (!dn || dn.status === 'Cancelled') return 0;
    const st = dn.status;
    if (st === 'Draft') return 0;
    if (st === 'Submitted') return 1;
    if (st === 'Approved') return 2;
    if (st === 'Posted') return 3;
    return 0;
  }, [dn]);

  if (!id) {
    navigate('/fin/debit-notes');
    return null;
  }

  if (isLoading && !dn) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!dn) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Text type="secondary">{t('debitNoteNotFound')}</Text>
        </Card>
      </div>
    );
  }

  const canSubmit = dn.status === 'Draft';
  const canApprove = dn.status === 'Submitted';
  const canPost = dn.status === 'Submitted' || dn.status === 'Approved';
  const canCancel = !['Posted', 'Cancelled'].includes(dn.status);

  const headerActions = (
    <Space wrap size="small">
      <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/debit-notes')}>
        {tc('back')}
      </Button>
      <ReportDocumentActions
        reportKey="supplier-debit-note"
        params={id ? { id } : null}
        documentId={id}
        sendPermissions={['finance.voucher.create']}
        emailLabel={t('email')}
      />
      {canSubmit && (
        <Button loading={actionLoading === 'submit'} onClick={() => runAction('submit', () => supplierDebitNoteService.submit(id!))}>
          {tc('submit')}
        </Button>
      )}
      {canApprove && (
        <Button
          type="primary"
          loading={actionLoading === 'approve'}
          icon={<CheckOutlined />}
          onClick={() => runAction('approve', () => supplierDebitNoteService.approve(id!))}
        >
          {tc('approve')}
        </Button>
      )}
      {canPost && (
        <Button type="primary" loading={actionLoading === 'post'} onClick={() => runAction('post', () => supplierDebitNoteService.post(id!))}>
          {t('postToGl')}
        </Button>
      )}
      {canCancel && (
        <Popconfirm title={t('cancelDebitNoteConfirm')} onConfirm={() => runAction('cancel', () => supplierDebitNoteService.cancel(id!))}>
          <Button danger icon={<StopOutlined />} loading={actionLoading === 'cancel'}>
            {tc('cancel')}
          </Button>
        </Popconfirm>
      )}
    </Space>
  );

  const rightRail = (
    <div>
      <Title level={5}>{t('quickLinks')}</Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/fin/debit-notes')}>
          {t('allDebitNotes')}
        </Button>
        {dn.purchase_return_id && (
          <Button
            type="link"
            block
            style={{ textAlign: 'left', paddingLeft: 0 }}
            onClick={() => navigate(`/scm/procurement/purchase-returns/${dn.purchase_return_id}`)}
          >
            {t('purchaseReturn')}
          </Button>
        )}
        {dn.supplier_invoice_id && (
          <Button
            type="link"
            block
            style={{ textAlign: 'left', paddingLeft: 0 }}
            onClick={() => navigate(`/scm/procurement/supplier-invoices/${dn.supplier_invoice_id}`)}
          >
            {t('supplierInvoice')}
          </Button>
        )}
      </Space>
    </div>
  );

  return (
    <div style={{ margin: '-20px -20px 0' }} className="debit-note-detail">
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel={t('supplierDebitNoteDocument')}
            documentNumber={dn.debit_note_number}
            statusTag={<Tag color={statusColors[dn.status] ?? 'default'}>{statusLabel(dn.status)}</Tag>}
            subtitle={<Text type="secondary">{dn.debit_note_date ? dayjs(dn.debit_note_date).format('DD MMM YYYY') : ''}</Text>}
            facts={
              <Space wrap>
                <Text>
                  <strong>{t('supplier')}:</strong> {dn.supplier?.supplier_name ?? dn.supplier_id ?? '—'}
                </Text>
                <Text>
                  <strong>{t('total')}:</strong> {formatCurrency(Number(dn.total_amount))}
                </Text>
              </Space>
            }
            primaryActions={headerActions}
          />
        }
        workflow={<WorkflowStrip stages={sdnWorkflowStages} current={sdnWorkflowCurrent} compact />}
        rightRail={rightRail}
      >
        <Collapse
          defaultActiveKey={['hdr', 'lines']}
          items={[
            {
              key: 'hdr',
              label: t('debitNoteAndReferences'),
              children: (
                <div>
                  <Card bordered={false} styles={{ body: { padding: 0 } }}>
                    <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
                      <Descriptions.Item label={t('date')}>{dn.debit_note_date ? dayjs(dn.debit_note_date).format('DD MMM YYYY') : '—'}</Descriptions.Item>
                      <Descriptions.Item label={t('supplier')}>{dn.supplier?.supplier_name ?? dn.supplier_id ?? '—'}</Descriptions.Item>
                      <Descriptions.Item label={t('type')}>{debitNoteTypeLabel(dn.debit_note_type)}</Descriptions.Item>
                      <Descriptions.Item label={t('subtotal')}>{formatCurrency(Number(dn.subtotal))}</Descriptions.Item>
                      <Descriptions.Item label={t('tax')}>{formatCurrency(Number(dn.tax_amount))}</Descriptions.Item>
                      <Descriptions.Item label={t('total')}>{formatCurrency(Number(dn.total_amount))}</Descriptions.Item>
                      {dn.purchaseReturn && (
                        <Descriptions.Item label={t('purchaseReturn')}>
                          <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/purchase-returns/${dn.purchase_return_id}`)}>
                            {dn.purchaseReturn.return_number}
                          </Button>
                        </Descriptions.Item>
                      )}
                      {dn.supplierInvoice && (
                        <Descriptions.Item label={t('supplierInvoice')}>
                          <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/supplier-invoices/${dn.supplier_invoice_id}`)}>
                            {dn.supplierInvoice.invoice_number}
                          </Button>
                        </Descriptions.Item>
                      )}
                      {dn.remarks && <Descriptions.Item label={t('remarks')} span={3}>{dn.remarks}</Descriptions.Item>}
                    </Descriptions>
                  </Card>
                </div>
              ),
            },
            {
              key: 'lines',
              label: t('lines'),
              children: (
                <div>
                  <Table<SupplierDebitNoteLineDTO>
                    size="small"
                    rowKey="id"
                    dataSource={dn.lines ?? []}
                    pagination={false}
                    scroll={{ x: 600 }}
                    columns={[
                      { title: t('description'), dataIndex: 'description', key: 'description', ellipsis: true },
                      { title: t('qty'), dataIndex: 'quantity', width: 80, align: 'right' as const, render: (v: number) => Number(v).toLocaleString() },
                      { title: t('rate'), dataIndex: 'unit_rate', width: 100, align: 'right' as const, render: (v: number) => formatCurrency(Number(v)) },
                      { title: t('amount'), dataIndex: 'amount', width: 110, align: 'right' as const, render: (v: number) => formatCurrency(Number(v)) },
                      { title: t('reason'), dataIndex: 'reason', ellipsis: true },
                    ]}
                  />
                </div>
              ),
            },
          ]}
        />
      </WorkspaceLayout>
    </div>
  );
}
